/**
 * AgentDrivenCampaignOptimizationAiNativePage — list of agent-proposed
 * campaign optimization suggestions with confidence bars + apply/dismiss.
 * GREEN accent throughout.
 */
import type { ReactNode } from "react";
import {
	useApplySuggestion,
	useDismissSuggestion,
	useOptimizationList,
} from "./hooks";
import {
	sortByConfidence,
	type OptimizationActionType,
	type OptimizationStatus,
	type OptimizationSuggestion,
} from "./api";

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const BTN_APPLY =
	"rounded-[6px] border border-green-700 bg-green-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_DISMISS =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";

const ACTION_LABEL: Record<OptimizationActionType, string> = {
	budget_shift: "Shift budget",
	bid_adjust: "Adjust bid",
	creative_swap: "Swap creative",
	pause: "Pause",
	resume: "Resume",
};

const STATUS_LABEL: Record<OptimizationStatus, string> = {
	proposed: "Proposed",
	applied: "Applied",
	rejected: "Dismissed",
};

function clampConfidence(n: number): number {
	if (!Number.isFinite(n)) return 0;
	if (n < 0) return 0;
	if (n > 1) return 1;
	return n;
}

function formatPct(n: number): string {
	return `${Math.round(clampConfidence(n) * 100)}%`;
}

function formatDate(unixSec: number): string {
	try {
		return new Date(unixSec * 1000).toISOString().slice(0, 16).replace("T", " ");
	} catch {
		return String(unixSec);
	}
}

function ConfidenceBar({ value }: { value: number }): ReactNode {
	const pct = clampConfidence(value) * 100;
	return (
		<div
			className="mt-2 h-2 w-full overflow-hidden rounded-full bg-zinc-100"
			data-testid="confidence-bar"
			role="progressbar"
			aria-valuenow={Math.round(pct)}
			aria-valuemin={0}
			aria-valuemax={100}
		>
			<div
				className="h-full bg-green-600"
				style={{ width: `${pct.toFixed(1)}%` }}
				data-testid="confidence-bar-fill"
			/>
		</div>
	);
}

function SuggestionRow({
	row,
	onApply,
	onDismiss,
	applyPending,
	dismissPending,
}: {
	row: OptimizationSuggestion;
	onApply: (actionId: string) => void;
	onDismiss: (actionId: string) => void;
	applyPending: boolean;
	dismissPending: boolean;
}): ReactNode {
	const disabled = row.status !== "proposed";
	return (
		<li
			className="flex flex-col gap-2 py-3"
			data-testid={`suggestion-row-${row.action_id}`}
		>
			<div className="flex flex-wrap items-start justify-between gap-3">
				<div className="flex flex-col gap-1">
					<span className="text-sm font-semibold text-zinc-900">
						{ACTION_LABEL[row.action_type]}
					</span>
					<span className="text-xs text-zinc-500">
						Campaign <span className="font-mono">{row.campaign_id}</span>
						<span className="mx-1.5 text-zinc-300">|</span>
						{formatDate(row.created_at)}
						<span className="mx-1.5 text-zinc-300">|</span>
						<span
							data-testid={`status-${row.action_id}`}
							className={
								row.status === "applied"
									? "font-medium text-green-700"
									: row.status === "rejected"
										? "text-zinc-500"
										: "text-zinc-700"
							}
						>
							{STATUS_LABEL[row.status]}
						</span>
					</span>
				</div>
				<div className="flex items-center gap-2">
					<button
						type="button"
						className={BTN_APPLY}
						onClick={() => onApply(row.action_id)}
						disabled={disabled || applyPending}
						data-testid={`apply-${row.action_id}`}
					>
						Apply
					</button>
					<button
						type="button"
						className={BTN_DISMISS}
						onClick={() => onDismiss(row.action_id)}
						disabled={disabled || dismissPending}
						data-testid={`dismiss-${row.action_id}`}
					>
						Dismiss
					</button>
				</div>
			</div>
			<div className="flex items-center gap-3">
				<div className="flex-1">
					<ConfidenceBar value={row.confidence} />
				</div>
				<span
					className="text-xs font-semibold text-green-700"
					data-testid={`confidence-pct-${row.action_id}`}
				>
					{formatPct(row.confidence)}
				</span>
			</div>
		</li>
	);
}

export function AgentDrivenCampaignOptimizationAiNativePage(): ReactNode {
	const listQuery = useOptimizationList();
	const applyMut = useApplySuggestion();
	const dismissMut = useDismissSuggestion();

	const items = listQuery.data?.items ?? [];
	const sorted = sortByConfidence(items);

	return (
		<main
			data-testid="agent-driven-campaign-optimization-ai-native-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					agent-driven-campaign-optimization-ai-native
				</p>
				<h1 className="text-3xl font-semibold text-green-700">
					Agent-Driven Campaign Optimization
				</h1>
				<p className="text-sm text-zinc-600">
					Review AI-proposed actions ordered by confidence. Apply to commit
					the action or dismiss to reject it.
				</p>
			</header>

			<section
				className={CARD}
				aria-label="Agent-driven optimization suggestions"
			>
				<div className="flex items-center justify-between">
					<h2 className="text-base font-semibold text-zinc-900">
						Suggestions
					</h2>
					<span
						className="text-xs text-zinc-500"
						data-testid="suggestion-count"
					>
						{sorted.length} total
					</span>
				</div>

				{listQuery.isLoading ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="agent-driven-campaign-optimization-ai-native-loading"
					>
						Loading suggestions.
					</p>
				) : listQuery.error ? (
					<p
						className="mt-3 text-sm text-red-600"
						role="alert"
						data-testid="agent-driven-campaign-optimization-ai-native-error"
					>
						{listQuery.error.message}
					</p>
				) : sorted.length === 0 ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="agent-driven-campaign-optimization-ai-native-empty"
					>
						No suggestions yet. The agent has nothing to recommend right now.
					</p>
				) : (
					<ul
						className="mt-3 divide-y divide-zinc-200"
						data-testid="agent-driven-campaign-optimization-ai-native-list"
					>
						{sorted.map((row) => (
							<SuggestionRow
								key={row.action_id}
								row={row}
								onApply={(id) => applyMut.mutate(id)}
								onDismiss={(id) => dismissMut.mutate(id)}
								applyPending={applyMut.isPending}
								dismissPending={dismissMut.isPending}
							/>
						))}
					</ul>
				)}

				{applyMut.error ? (
					<p
						className="mt-3 text-xs text-red-600"
						role="alert"
						data-testid="apply-error"
					>
						{applyMut.error.message}
					</p>
				) : null}
				{dismissMut.error ? (
					<p
						className="mt-3 text-xs text-red-600"
						role="alert"
						data-testid="dismiss-error"
					>
						{dismissMut.error.message}
					</p>
				) : null}
			</section>
		</main>
	);
}

export default AgentDrivenCampaignOptimizationAiNativePage;
